import { useMemo, useState } from "react"
import { useAuthContext } from "@/context/authentication/auth-context"
import { RelacaoPagamentosFormData } from "../components/form-relacao-pagamentos/schemas"
import { useSave, useUploadNF } from "../queries/relacao-pagamentos-uses"
import { useGetContractsByMovie } from "@/services/queries/useContratos"
import { initFormRelacaoPagamentos, mapFormToPayloadSave } from "../helpers/relacao-pagamentos-helpers"
import { useLocation } from "wouter"
import { useFindItemsByStep } from "@/lib/queries/orcamento-use-lib"
import { MembroEquipe } from "@/pages/contrato/contrato.types"

export function useRelacaoPagamentosForm(movieId: number, budgetId: number, stepId: number) {
  const [_, navigate] = useLocation()

  const { person } = useAuthContext()

  const { mutateAsync: uploadNF } = useUploadNF()
  const { mutateAsync: save, isPending } = useSave(() => navigate("/financeiro/relacao-pagamentos"))
  const { data: contracts } = useGetContractsByMovie(movieId)
  const { data: budgetItems } = useFindItemsByStep(budgetId, stepId)

  const contratos = useMemo(() => {
    if (!contracts) return []
    const seen = new Set<number>()
    return contracts.data.reduce((acc: typeof contracts.data, c) => {
      if (seen.has(c.staffUser?.person?.id ?? 0)) return acc
      seen.add(c.staffUser?.person?.id ?? 0)
      acc.push(c)
      return acc
    }, [])
  }, [contracts])

  const equipe: MembroEquipe[] = useMemo(() => {
    if (!contracts?.data) return []
    const seen = new Set<number>()
    return contracts.data.reduce<MembroEquipe[]>((acc, c) => {
      const id = c.staffUser?.person?.id ?? 0
      if (seen.has(id)) return acc
      seen.add(id)
      acc.push({
        id,
        name: c.staffUser?.person?.name ?? "Sem nome",
        email: c.staffUser?.login ?? "Sem email",
        document: c.staffUser?.person?.document ?? "Sem documento",
        role: c.staffUser?.person?.role ?? "Sem função",
        pjName: c.staffUser?.person?.pjName ?? "Sem razão social",
        pjDocument: c.staffUser?.person?.pjDocument ?? "Sem documento",
      })
      return acc
    }, [])
  }, [contracts])

  const [fiscalDocUrl, setFiscalDocUrl] = useState<string>("")

  const form = initFormRelacaoPagamentos(person?.name || "")

  const handleFileUpload = async (file: File | null, contratoId: number) => {
    if (!file) return

    try {
      const formData = new FormData()
      formData.append("file", file)
      const response = await uploadNF({ file: formData, contractId: contratoId })
      setFiscalDocUrl(response.url)
    } catch (error) {
      console.error("Erro ao fazer upload do documento:", error)
    }
  }

  const onSubmit = async (data: RelacaoPagamentosFormData) => {
    const paymentDateString =
      typeof data.payment_date === "string"
        ? data.payment_date
        : data.payment_date.toISOString()

    const payload = mapFormToPayloadSave({ ...data, fiscal_note_url: fiscalDocUrl, payment_date: paymentDateString })

    await save(payload);
  }

  return {
    form,
    person,
    isPending,
    budgetItems: budgetItems?.data,
    contratos,
    equipe,
    handleFileUpload,
    onSubmit,
    navigate
  }
}
